import { computed, defineComponent, ref, watch } from 'vue'
import { useMainStore } from '@/store/main/main'
import { useApiCall } from '@/composables/useApiCall'
import { getCallForHelpApiCall } from '@/api/app'
import { getStorageItemWithExpiry, setStorageItemWithExpiry } from '@/helpers/localStorage'
import type { Position } from '@/types/main'
import type { GetCallHelpSuccessResponse } from '@/types/app'
import type { DefaultError } from '@/types/httpError'

export default defineComponent({
  name: 'CallWidget',
  setup() {
    const SKIP_FLAG_KEY = 'shouldSkipGetCall'
    const mainStore = useMainStore()

    const isMobile = computed(() => mainStore.isMobile)

    const menuOpen = ref<boolean>(true)
    const position = ref<Position>({ x: 26, y: isMobile.value ? 43 : 26 })
    const dragging = ref<boolean>(false)
    const offset = ref<Position>({ x: 26, y: isMobile.value ? 43 : 26 })
    const isConfirmPopupOpen = ref(false)

    const onConfirmPopupClose = () => {
      isConfirmPopupOpen.value = false
    }
    const startDrag = (event: MouseEvent): void => {
      dragging.value = true
      offset.value = {
        x: -(event.clientX + position.value.x),
        y: -(event.clientY + position.value.y),
      }
      document.addEventListener('mousemove', onDrag)
      document.addEventListener('mouseup', stopDrag)
    }

    const onDrag = (event: MouseEvent): void => {
      if (dragging.value) {
        position.value = {
          x: -(event.clientX + offset.value.x),
          y: -(event.clientY + offset.value.y),
        }
      }
    }

    const stopDrag = () => {
      dragging.value = false
      document.removeEventListener('mousemove', onDrag)
      document.removeEventListener('mouseup', stopDrag)
    }

    const {
      data: callData,
      error: callError,
      executeApiCall: getCall,
    } = useApiCall<GetCallHelpSuccessResponse, DefaultError>(getCallForHelpApiCall, true)

    const callManager = async () => {
      if (menuOpen.value) {
        try {
          const shouldSkipGetCall = getStorageItemWithExpiry<string>(SKIP_FLAG_KEY)

          if (!shouldSkipGetCall) {
            await getCall()
            setStorageItemWithExpiry(SKIP_FLAG_KEY, 'true', 1800000)
          }
          isConfirmPopupOpen.value = true
        } catch {
          if (callError.value) {
            console.error(callError.value)
          }
        }
      } else {
        menuOpen.value = true
      }
    }

    watch(
      [dragging, position],
      ([newParam1, newParam2], [_, oldParam2]) => {
        if (newParam1 && JSON.stringify(oldParam2) !== JSON.stringify(newParam2)) {
          menuOpen.value = false
        }
      },
      { deep: true },
    )

    return {
      menuOpen,
      isMobile,
      startDrag,
      onDrag,
      stopDrag,
      position,
      dragging,
      callManager,
      isConfirmPopupOpen,
      onConfirmPopupClose,
    }
  },
})
